import React, { useEffect, useState } from "react";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Grid,
  Divider,
} from "@mui/material";
import { ShoppingBag, Visibility, Edit, Refresh } from "@mui/icons-material";
import { apiList, invokeApi, invokeGetApi } from "../../../services/apiServices";

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newOrderStatus, setNewOrderStatus] = useState("");
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const demoOrders = [
    {
      id: 1,
      order_number: "ORD-2026-1001",
      customer_name: "Rahul Sharma",
      customer_email: "rahul@example.com",
      customer_phone: "9876543210",
      total_amount: 499.00,
      payment_status: "Paid",
      order_status: "Completed",
      shipping_address: "123 Main St, Indiranagar, Bangalore - 560038",
      created_at: "2026-09-30 10:00:00",
      items: [{ product_name: "Windows 10 Pro Retail Key", price: 499.00, quantity: 1, total: 499.00 }]
    },
    {
      id: 2,
      order_number: "ORD-2026-1002",
      customer_name: "Anita Verma",
      customer_email: "anita@example.com",
      customer_phone: "9123456789",
      total_amount: 1499.00,
      payment_status: "Paid",
      order_status: "Processing",
      shipping_address: "45 Park Avenue, Banjara Hills, Hyderabad - 500034",
      created_at: "2026-09-30 11:30:00",
      items: [{ product_name: "MS Office 2024 Home & Business", price: 1499.00, quantity: 1, total: 1499.00 }]
    }
  ];

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await invokeGetApi(apiList.getOrders || "/getOrders");
      if (response?.data?.responseCode === "200" && response?.data?.orders?.length > 0) {
        setOrders(response.data.orders);
      } else {
        setOrders(demoOrders);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders(demoOrders);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleOpenDetail = (order) => {
    setSelectedOrder(order);
    setOpenDetailDialog(true);
  };

  const handleOpenStatus = (order) => {
    setSelectedOrder(order);
    setNewOrderStatus(order.order_status);
    setNewPaymentStatus(order.payment_status);
    setOpenStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      await invokeApi(apiList.updateOrder || "/updateOrder", {
        order_id: selectedOrder.id,
        order_status: newOrderStatus,
        payment_status: newPaymentStatus,
      });
      setOpenStatusDialog(false);
      fetchOrders();
    } catch (error) {
      console.error("Failed to update order status:", error);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return "success";
      case "processing": return "warning";
      case "pending": return "info";
      case "cancelled": return "error";
      default: return "default";
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_phone?.includes(searchQuery);

    const matchesStatus =
      statusFilter === "ALL" || order.order_status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ display: "flex", bgcolor: "#F9FAF4", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ShoppingBag sx={{ fontSize: 36, color: "#1E40AF" }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1E40AF" }}>
                Orders Manager
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchOrders}
              sx={{ borderColor: "#1E40AF", color: "#1E40AF" }}
            >
              Refresh Orders
            </Button>
          </Box>

          {/* Search & Filter Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #EAEAEA", display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search by Order #, Customer Name, Phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Order Status</InputLabel>
              <Select
                value={statusFilter}
                label="Order Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 2 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead sx={{ bgcolor: "#F3F4F6" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress sx={{ color: "#1E40AF" }} />
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#666" }}>
                        No orders found matching your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell sx={{ fontWeight: 600, color: "#1E40AF" }}>
                          {order.order_number}
                        </TableCell>
                        <TableCell>{order.customer_name}</TableCell>
                        <TableCell>{order.customer_phone}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>₹{order.total_amount}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.payment_status}
                            size="small"
                            color={order.payment_status === "Paid" ? "success" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.order_status}
                            size="small"
                            color={getStatusChipColor(order.order_status)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#666" }}>
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleOpenDetail(order)}
                            sx={{ mr: 1, color: "#1E40AF" }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Edit />}
                            color="secondary"
                            onClick={() => handleOpenStatus(order)}
                          >
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Box>

      {/* Order Detail Modal */}
      <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE" }}>
              Order Details - {selectedOrder.order_number}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Customer Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.customer_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone / Email</Typography>
                  <Typography variant="body1">{selectedOrder.customer_phone} | {selectedOrder.customer_email || "N/A"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Shipping Address</Typography>
                  <Typography variant="body1">{selectedOrder.shipping_address}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Items Ordered</Typography>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#FAFAFA" }}>
                      <TableRow>
                        <TableCell>Product Name</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="right">₹{item.price}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{item.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">No item breakdown available.</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: "1px solid #EEE" }}>
              <Button onClick={() => setOpenDetailDialog(false)} variant="contained" sx={{ bgcolor: "#1E40AF" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update Order Status</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={newOrderStatus}
              label="Order Status"
              onChange={(e) => setNewOrderStatus(e.target.value)}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Processing">Processing</MenuItem>
              <MenuItem value="Shipped">Shipped</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={newPaymentStatus}
              label="Payment Status"
              onChange={(e) => setNewPaymentStatus(e.target.value)}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Refunded">Refunded</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updating}
            sx={{ bgcolor: "#1E40AF" }}
          >
            {updating ? "Updating..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderList;
